import { useQuery } from "@tanstack/react-query";
import { address as toAddress } from "@solana/kit";
import { tokenService } from "../services/token";
import { useBrowserWalletStore } from "@/features/wallet";
import { useAuthStore } from "@/store/auth-store";
import type { TokenAccount } from "../types/solana";

interface UseSolanaTokenAccountsOptions {
  /** Force refetch when component mounts (useful for balance-critical views) */
  refreshOnMount?: boolean;
}

/**
 * Hook to fetch SPL token accounts for the connected wallet.
 */
export function useSolanaTokenAccounts(
  options?: UseSolanaTokenAccountsOptions,
) {
  const publicKey = useBrowserWalletStore((state) => state.publicKey);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const query = useQuery({
    queryKey: ["solana-token-accounts", publicKey],
    queryFn: async (): Promise<TokenAccount[]> => {
      if (!publicKey) {
        throw new Error("No wallet connected");
      }
      return tokenService.getTokenAccounts(toAddress(publicKey));
    },
    enabled: publicKey !== null && isAuthenticated,
    refetchOnMount: options?.refreshOnMount ? "always" : false,
    staleTime: 60_000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return {
    tokenAccounts: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
